////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ft3d/ft3d-scenery-picker.ts — Scenery / Interior tile pickers + backdrop-seed controls for
// tools/encounters/ft3d_scene_host.php.
// ----------------------------------------------------------------------------------------------------
// Client-side replacement (2026-07-07) for the three inline <script> blocks + the PHP mirrors of
// FT3D_BG_VARIANT_CATALOG / INDOOR_MANIFEST the form used to hand-maintain ("keep in sync" comments;
// the thumb cache-bust had already drifted to v8 vs BG_VER 13). All picker data now comes from
// ft3d-bg-manifest.ts (ft3dBgPickerTiles / ft3dIndoorPickerTiles) — one source of truth, no drift class.
//
// Ships in the pre-template bundle (encounter-map/index.ts side-effect import) and self-initializes
// at DOMContentLoaded ONLY when the form's #ft_scenery_tiles mount exists — a no-op on every other
// page. Init is deferred to DOMContentLoaded because the env select stores environment UUIDs and
// the UUID → variable_name map (getEnvironmentName / ENVIRONMENTS) lives in template.js, which
// parses AFTER this bundle's tag; by DOMContentLoaded every classic script has run.
//
// Element contract (IDs owned by ft3d_scene_host.php; the side-panels-init sync functions read
// the same hidden inputs — do not rename): #encounter_environment (select, UUID values),
// #encounter_backdrop_variant / #encounter_backdrop_seed / #encounter_interior_type (hidden),
// #ft_scenery_tiles / #ft_interior_tiles (mounts), #ft_interior_row (+ #encounter_inside toggle),
// #ft_backdrop_reroll / #ft_backdrop_reset / #ft_backdrop_custom_badge, #ft_backdrop_tint.
// Every change invokes modifyEncounter() to save the selected backdrop.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import { loadApprovedSurfaceMaterial, ft3dBgPickerTiles, ft3dIndoorPickerTiles } from './ft3d-bg-manifest';
import type { FT3DPickerTile } from './ft3d-bg-manifest';

// template.js / side-panels-init globals, looked up lazily (defined by the time init runs).
type FTPickerWindow = Window & {
    modifyEncounter?: () => void;
    getEnvironmentName?: (uuid: string) => string;
    ftSetBackdropPalette?: (mode: string) => void;
};

function fireModifyEncounter(): void {
    const w = window as FTPickerWindow;
    if (typeof w.modifyEncounter === 'function') w.modifyEncounter();
}

/** Env UUID (the select's value) → manifest variable_name; '' when the map isn't loaded. */
function envNameFromUuid(uuid: string): string {
    const w = window as FTPickerWindow;
    return uuid && typeof w.getEnvironmentName === 'function' ? w.getEnvironmentName(uuid) : '';
}

/**
 * Shared tile-strip renderer (the scenery + interior pickers are the same widget — the old
 * inline scripts carried two near-identical copies). Click = write the hidden field, restyle,
 * save. `dataAttr` keeps the historical per-picker attribute names (data-variant/data-interior).
 */
const tileObservers = new WeakMap<HTMLElement, IntersectionObserver>();

function renderTiles(
    wrap: HTMLElement,
    hidden: HTMLInputElement,
    tiles: FT3DPickerTile[],
    dataAttr: string,
): void {
    tileObservers.get(wrap)?.disconnect();
    const thumbs = new WeakMap<Element, string>();
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(entries => {
        for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const thumb = thumbs.get(entry.target);
            if (thumb) (entry.target as HTMLElement).style.backgroundImage = `url("${thumb}")`;
            observer?.unobserve(entry.target);
        }
    }, { rootMargin: '100px' });
    if (observer) tileObservers.set(wrap, observer);
    wrap.innerHTML = '';
    for (const t of tiles) {
        const el = document.createElement('button');
        el.type = 'button';
        const selected = t.key === hidden.value;
        el.className = 'ft-scenery-tile' + (selected ? ' selected' : '');
        el.setAttribute(dataAttr, t.key);
        el.setAttribute('aria-pressed', selected ? 'true' : 'false');
        el.setAttribute('aria-label', t.label);
        if (t.color) el.style.backgroundColor = t.color;
        // Campaign play carries this hidden editor. Its full backdrop catalog
        // must not compete with the scene's modules/textures on every page load.
        if (t.thumb) {
            if (observer) thumbs.set(el, t.thumb);
            else el.style.backgroundImage = `url("${t.thumb}")`;
        }
        const lab = document.createElement('span');
        lab.className = 'ft-scenery-tile-label';
        lab.textContent = t.label;
        el.appendChild(lab);
        el.addEventListener('click', () => {
            if (hidden.value === t.key) return;
            hidden.value = t.key;
            for (const c of Array.from(wrap.children)) {
                const on = c.getAttribute(dataAttr) === t.key;
                c.classList.toggle('selected', on);
                c.setAttribute('aria-pressed', on ? 'true' : 'false');
            }
            fireModifyEncounter();
        });
        wrap.appendChild(el);
        if (t.thumb && observer) observer.observe(el);
    }
}

/** Scenery / Backdrop variant picker — re-renders on env change; a saved variant that no
 *  longer fits the env resets to '' (env default) BEFORE the env change's own save reads
 *  the form, so one save carries both fields (no second save, no out-of-order race —
 *  Codex review 2026-07-07). */
function initSceneryPicker(): void {
    const wrap      = document.getElementById('ft_scenery_tiles');
    const hidden    = document.getElementById('encounter_backdrop_variant') as HTMLInputElement | null;
    const envSelect = document.getElementById('encounter_environment') as HTMLSelectElement | null;
    if (!wrap || !hidden || !envSelect) return;

    const render = (): void => {
        let tiles = ft3dBgPickerTiles(envNameFromUuid(envSelect.value));
        // Only reset a no-longer-fitting variant when the env actually RESOLVED to catalog
        // tiles — an unresolved env (no env picked yet / UUID map missing) must never blank
        // a saved variant, or the next unrelated save would persist the wipe.
        const resolved = tiles.length > 0;
        if (!tiles.length) tiles = [{ key: '', label: 'Misty', thumb: '' }];
        if (resolved && !tiles.some((t) => t.key === hidden.value)) {
            hidden.value = '';
        }
        renderTiles(wrap, hidden, tiles, 'data-variant');
    };

    // CAPTURE phase: at the target, capture listeners fire BEFORE bubble listeners (DOM spec),
    // so the variant reset above lands before the select's inline onchange="modifyEncounter();"
    // (a bubble handler) serializes the form — the env change's own save already carries the
    // corrected variant. The manifest fallback chain keeps any unsaved stale value harmless.
    envSelect.addEventListener('change', render, true);
    render();
}

/** Interior / room-type picker — row shown only while the Inside checkbox is checked. */
function initInteriorPicker(): void {
    const wrap   = document.getElementById('ft_interior_tiles');
    const hidden = document.getElementById('encounter_interior_type') as HTMLInputElement | null;
    const row    = document.getElementById('ft_interior_row');
    if (!wrap || !hidden || !row) return;

    renderTiles(wrap, hidden, ft3dIndoorPickerTiles(), 'data-interior');
    // Preserve a saved database-backed semantic surface without expanding the entire catalog.
    // The categorized library is disclosed from Rooms in Scene Builder.
    if (hidden.value) void loadApprovedSurfaceMaterial(hidden.value).then((registered) => {
        if (registered) renderTiles(wrap, hidden, ft3dIndoorPickerTiles(), 'data-interior');
    });

    const inside = document.getElementById('encounter_inside') as HTMLInputElement | null;
    const toggleRow = (): void => {
        row.style.display = inside?.checked ? '' : 'none';
    };
    inside?.addEventListener('change', toggleRow);
    toggleRow();
}

/** 🎲 re-roll / ↺ reset-look / "custom look" badge for encounter_backdrop_seed
 *  (PROCEDURAL_BACKDROP_PLAN.md §2). Blank seed = the encounter-id-derived default look —
 *  a distinguishable state, which is why reset writes '' rather than another token. */
function initBackdropSeedControls(): void {
    const seed   = document.getElementById('encounter_backdrop_seed') as HTMLInputElement | null;
    const reroll = document.getElementById('ft_backdrop_reroll');
    if (!seed || !reroll) return;
    const reset = document.getElementById('ft_backdrop_reset');
    const badge = document.getElementById('ft_backdrop_custom_badge');
    // Visible seed field (owner 2026-07-09): see / type / randomize the seed instead of it being an
    // opaque hidden token. Mirrors the hidden #encounter_backdrop_seed both ways.
    const input = document.getElementById('ft_backdrop_seed_input') as HTMLInputElement | null;

    // Server contract: ^[a-z0-9]{1,16}$ (blank = default). Sanitize typed input to that alphabet.
    const clean = (v: string): string => v.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 16);

    const syncSeedUi = (): void => {
        const custom = seed.value.trim() !== '';
        if (reset) reset.style.display = custom ? '' : 'none';
        if (badge) badge.style.display = custom ? '' : 'none';
        if (input && input.value !== seed.value) input.value = seed.value;
    };

    reroll.addEventListener('click', () => {
        // Fresh short base36 token (matches the ^[a-z0-9]{1,16}$ server validation).
        seed.value = Math.floor(Math.random() * Math.pow(36, 8)).toString(36);
        syncSeedUi();
        fireModifyEncounter();
    });
    reset?.addEventListener('click', () => {
        if (seed.value === '') return;
        seed.value = '';
        syncSeedUi();
        fireModifyEncounter();
    });
    // Live-sanitize as the GM types (keeps the field to the server alphabet); commit + save on
    // change/blur or Enter so we don't fire a save (and a WS push) on every keystroke.
    if (input) {
        input.addEventListener('input', () => {
            const c = clean(input.value);
            if (input.value !== c) input.value = c;
        });
        const commit = (): void => {
            const c = clean(input.value);
            input.value = c;
            if (c === seed.value) return;   // no change → no save
            seed.value = c;
            syncSeedUi();
            fireModifyEncounter();
        };
        input.addEventListener('change', commit);
        input.addEventListener('keydown', (e) => {
            if ((e as KeyboardEvent).key === 'Enter') { e.preventDefault(); commit(); }
        });
    }
    syncSeedUi();
}

/** Backdrop tint — a personal VIEW pref (localStorage ft_xz_bg_palette), never saved to the
 *  encounter. ftSetBackdropPalette (attached by ft3d-bg-manifest.ts, same bundle) re-stamps any
 *  live backdrop roots; the localStorage fallback covers a missing global defensively. */
function initBackdropTint(): void {
    const sel = document.getElementById('ft_backdrop_tint') as HTMLSelectElement | null;
    if (!sel) return;
    try {
        sel.value = window.localStorage?.getItem('ft_xz_bg_palette') || 'auto';
    } catch { /* ignore */ }
    sel.addEventListener('change', () => {
        const w = window as FTPickerWindow;
        if (typeof w.ftSetBackdropPalette === 'function') {
            w.ftSetBackdropPalette(sel.value);
        } else {
            try { window.localStorage?.setItem('ft_xz_bg_palette', sel.value); } catch { /* ignore */ }
        }
    });
}

function initSceneryPickers(): void {
    if (!document.getElementById('ft_scenery_tiles')) return;   // not the modify_encounter form
    initSceneryPicker();
    initInteriorPicker();
    initBackdropSeedControls();
    initBackdropTint();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSceneryPickers, { once: true });
} else {
    initSceneryPickers();
}
